import { ExternalLink, KeyRound, ShieldCheck, Trash2, X } from "lucide-react";
import { useState, type KeyboardEvent, type MouseEvent } from "react";

interface SettingsDialogProps {
  currentKey: string;
  onClose: () => void;
  onValidateAndSave: (value: string) => Promise<void>;
  onClear: () => void;
}

export function SettingsDialog({ currentKey, onClose, onValidateAndSave, onClear }: SettingsDialogProps) {
  const [value, setValue] = useState(currentKey);
  const [validating, setValidating] = useState(false);

  const handleSave = async () => {
    if (!value.trim()) return;
    setValidating(true);
    try {
      await onValidateAndSave(value.trim());
    } finally {
      setValidating(false);
    }
  };

  const handleBackdrop = (event: MouseEvent<HTMLDialogElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === "Escape") onClose();
  };

  return (
    <dialog open className="fixed inset-0 z-50 m-0 grid size-full max-h-none max-w-none place-items-center bg-black/45 p-4 backdrop:bg-black/45" aria-labelledby="settings-title" onMouseDown={handleBackdrop} onKeyDown={handleKeyDown}>
      <div className="w-full max-w-xl border border-line-strong bg-paper shadow-dialog">
        <div className="flex items-start justify-between border-b border-line px-5 py-4">
          <div>
            <p className="eyebrow">Local settings</p>
            <h2 id="settings-title" className="mt-1 font-display text-2xl font-medium text-ink">YouTube API access</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close settings"><X aria-hidden="true" size={18} /></button>
        </div>

        <div className="space-y-5 px-5 py-5">
          <div className="flex gap-3 border border-valid/30 bg-valid-soft px-3 py-3 text-sm text-valid">
            <ShieldCheck aria-hidden="true" className="mt-0.5 shrink-0" size={18} />
            <p className="leading-relaxed">The key stays in this browser’s local storage. ProTV Converter has no server and sends the key only to Google’s YouTube Data API.</p>
          </div>

          <div>
            <label className="field-label" htmlFor="youtube-api-key">YouTube Data API v3 key</label>
            <div className="relative">
              <KeyRound aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={16} />
              <input id="youtube-api-key" className="field pl-9 font-mono" type="password" autoComplete="off" value={value} onChange={(event) => setValue(event.target.value)} placeholder="AIza..." autoFocus />
            </div>
          </div>

          <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed text-muted">
            <li>Create or select a project in Google Cloud.</li>
            <li>Enable YouTube Data API v3 and create an API key.</li>
            <li>For a browser-restricted key, allow the deployed GitHub Pages origin.</li>
          </ol>

          <a className="secondary-button w-fit" href="https://console.cloud.google.com/apis/library/youtube.googleapis.com" target="_blank" rel="noreferrer">Open Google Cloud <ExternalLink aria-hidden="true" size={15} /></a>
        </div>

        <div className="flex flex-wrap justify-between gap-2 border-t border-line bg-canvas/55 px-5 py-4">
          <button className="secondary-button text-danger" type="button" onClick={() => { onClear(); setValue(""); }} disabled={!currentKey}><Trash2 aria-hidden="true" size={15} /> Remove saved key</button>
          <div className="flex gap-2">
            <button className="secondary-button" type="button" onClick={onClose}>Cancel</button>
            <button className="primary-button" type="button" onClick={() => void handleSave()} disabled={!value.trim() || validating}>{validating ? "Checking key" : "Validate and save"}</button>
          </div>
        </div>
      </div>
    </dialog>
  );
}
